import type { ArticlePost, Lang } from '@/types';
import type { SeoConfig, SeoAlternates } from '@hooks/useSeo';
import { pathFor } from '@/i18n';

const ORIGIN = 'https://pierresere.com';

/** URI stables des deux entites — ce qui permet a Google de les relier. */
export const PERSON_ID = `${ORIGIN}/#pierre`;
export const ORG_ID = 'https://solutions-asterion.com/#organization';

/**
 * Paire d'URL d'une meme page dans les deux langues.
 *
 * Sert a poser les `hreflang` reciproques : declarer l'alternance d'un seul
 * cote ne suffit pas, Google ignore une declaration non confirmee par la page
 * visee. `x-default` pointe vers le francais, langue d'origine du site.
 */
export function alternatesFor(route: string): SeoAlternates {
  return {
    fr: `${ORIGIN}${pathFor(route, 'fr')}`,
    en: `${ORIGIN}${pathFor(route, 'en')}`,
  };
}

function canonicalFor(route: string, lang: Lang): string {
  return `${ORIGIN}${pathFor(route, lang)}`;
}

// Le site cible les requetes de PERSONNE ; solutions-asterion.com garde les
// requetes produit. Aucune promesse commerciale ici : ce site dit qui est
// Pierre, le site d'entreprise dit ce qu'Asterion vend.
const HOME_TEXT: Record<Lang, { title: string; description: string }> = {
  fr: {
    title:
      'Pierre Séré — Technicien en génie mécanique et inspecteur qualité à Sorel-Tracy · Fondateur d’Asterion Solutions',
    description:
      'Pierre Séré, technicien en génie mécanique à Sorel-Tracy (Montérégie, Québec) : 14 ans de qualité industrielle — inspection dimensionnelle GD&T, machiniste CNC, coordination NDT, ISO 9001 — chez Hydro-Québec, GE, Voith et Andritz. Fondateur d’Asterion Solutions, lauréat OSEntreprendre 2026.',
  },
  en: {
    title:
      'Pierre Séré — Mechanical engineering technician and quality inspector in Sorel-Tracy · Founder of Asterion Solutions',
    description:
      'Pierre Séré, mechanical engineering technician in Sorel-Tracy (Montérégie, Quebec): 14 years of industrial quality work — GD&T dimensional inspection, CNC machining, NDT coordination, ISO 9001 — for Hydro-Québec, GE, Voith and Andritz. Founder of Asterion Solutions, OSEntreprendre 2026 laureate.',
  },
};

const ASTERION_TEXT: Record<Lang, { title: string; description: string }> = {
  fr: {
    title: 'Pierre Séré et Asterion Solutions — pourquoi j’ai fondé l’entreprise',
    description:
      'Comment un technicien qualité a construit les outils que son atelier n’avait pas, et comment c’est devenu Asterion Solutions. L’histoire du fondateur, par lui-même.',
  },
  en: {
    title: 'Pierre Séré and Asterion Solutions — why I founded the company',
    description:
      'How a quality technician built the tools his shop did not have, and how that became Asterion Solutions. The founder’s story, in his own words.',
  },
};

const ARTICLES_TEXT: Record<Lang, { title: string; description: string }> = {
  fr: {
    title: 'Articles de Pierre Séré — qualité industrielle et IA appliquée',
    description:
      'Articles de fond signés Pierre Séré sur la qualité industrielle, l’ISO 9001 et l’IA appliquée dans les PME manufacturières du Québec.',
  },
  en: {
    title: 'Articles by Pierre Séré — industrial quality and applied AI',
    description:
      'In-depth articles by Pierre Séré on industrial quality, ISO 9001 and applied AI in Quebec’s small and mid-sized manufacturers.',
  },
};

export function seoHome(lang: Lang): SeoConfig {
  return {
    ...HOME_TEXT[lang],
    canonical: canonicalFor('/', lang),
    ogType: 'profile',
    lang,
    alternates: alternatesFor('/'),
  };
}

export function seoAsterion(lang: Lang): SeoConfig {
  return {
    ...ASTERION_TEXT[lang],
    canonical: canonicalFor('/asterion', lang),
    ogType: 'article',
    lang,
    alternates: alternatesFor('/asterion'),
  };
}

export function seoArticlesIndex(lang: Lang): SeoConfig {
  return {
    ...ARTICLES_TEXT[lang],
    canonical: canonicalFor('/articles', lang),
    ogType: 'website',
    lang,
    alternates: alternatesFor('/articles'),
  };
}

/**
 * JSON-LD Article. `mainEntityOfPage` pointe vers le canonical Asterion :
 * c'est la page de reference, ce site n'est que la vitrine d'auteur.
 */
export function articleJsonLd(post: ArticlePost): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    articleSection: post.category,
    inLanguage: 'fr-CA',
    author: { '@id': PERSON_ID },
    publisher: { '@id': ORG_ID },
    mainEntityOfPage: { '@type': 'WebPage', '@id': post.canonical },
    image: `${ORIGIN}/og-image.png`,
  };
}

/** Fil d'Ariane : Accueil > Articles > Titre. */
export function breadcrumbJsonLd(post: ArticlePost): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: 'Articles', item: `${ORIGIN}/articles` },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: `${ORIGIN}/articles/${post.slug}`,
      },
    ],
  };
}

/**
 * SeoConfig complet d'un article, JSON-LD compris.
 *
 * Les articles n'existent qu'en francais ici : pas d'`alternates`, et le
 * canonical reste cross-domain vers le blog Asterion, domicile canonique.
 */
export function seoForArticle(post: ArticlePost): SeoConfig {
  return {
    title: `${post.title} · Pierre Séré`,
    description: post.excerpt,
    canonical: post.canonical,
    ogType: 'article',
    lang: 'fr',
    jsonLd: [articleJsonLd(post), breadcrumbJsonLd(post)],
  };
}
